import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type {} from "@redux-devtools/extension"; // required for devtools typing
import { ArrowToDraw, Attempt, Evaluation, Level, MoveWithComment, PlayerColor, Settings } from "./models";
import { OtAttempt2 } from "./PositionStats";
import { Move } from "chess.js";

interface State {
    attempts: Attempt[];
    setAttempts: (attempts: Attempt[]) => void;
    levels: Level[];
    setLevels: (levels: Level[]) => void;
    playerColor: PlayerColor;
    setPlayerColor: (playerColor: PlayerColor) => void;
    illegalMoveInMenu: boolean;
    setIllegalMoveInMenu: (illegalMoveInMenu: boolean) => void;
    showFirstGameModal: boolean;
    setShowFirstGameModal: (showFirstGameModal: boolean) => void;
    arrowToDraw: ArrowToDraw | undefined;
    setArrowToDraw: (arrowToDraw: ArrowToDraw | undefined) => void;
    uniquePositionAgainstPlayer: boolean;
    setUniquePositionAgainstPlayer: (uniquePositionAgainstPlayer: boolean) => void;
    openedSettings: boolean;
    setOpenedSettings: (openedSettings: boolean) => void;
    openedEditModal: boolean;
    setOpenedEditModal: (openedEditModal: boolean) => void;
    moveHistory: MoveWithComment[];
    setMoveHistory: (moveHistory: MoveWithComment[]) => void;
    evaluations: Record<number, Evaluation>;
    addEvaluation: (halfMoveNumber: number, evaluation: Evaluation) => void;
    setEvaluations: (evaluations: Record<number, Evaluation>) => void;
    redoStack: MoveWithComment[];
    setRedoStack: (redoStack: MoveWithComment[]) => void;
    bestMoves: string[];
    setBestMoves: (bestMoves: string[]) => void;
    addBestMove: (bestMove: string) => void;
    stockfishEval:
        | { positionEvaluation: string; depth: number; time: string; nps: string; isComplete: boolean }
        | undefined;
    setStockfishEval: (
        stockfishEval:
            | { positionEvaluation: string; depth: number; time: string; nps: string; isComplete: boolean }
            | undefined,
    ) => void;
    bufferedMove: Move | undefined;
    setBufferedMove: (bufferedMove: Move | undefined) => void;
}
export const useStorage = create<State>()(
    // devtools(
    // persist(
    (set) => ({
        attempts: [],
        setAttempts: (attempts: Attempt[]) => set(() => ({ attempts })),
        levels: ["1600"],
        setLevels: (levels: Level[]) => set(() => ({ levels })),
        playerColor: "w",
        setPlayerColor: (playerColor: PlayerColor) => set(() => ({ playerColor })),
        illegalMoveInMenu: false,
        setIllegalMoveInMenu: (illegalMoveInMenu: boolean) => set(() => ({ illegalMoveInMenu })),
        showFirstGameModal: false,
        setShowFirstGameModal: (showFirstGameModal: boolean) => set(() => ({ showFirstGameModal })),
        arrowToDraw: undefined,
        setArrowToDraw: (arrowToDraw: ArrowToDraw | undefined) => set(() => ({ arrowToDraw })),
        uniquePositionAgainstPlayer: false,
        setUniquePositionAgainstPlayer: (uniquePositionAgainstPlayer: boolean) =>
            set(() => ({ uniquePositionAgainstPlayer })),
        openedSettings: false,
        setOpenedSettings: (openedSettings: boolean) => set(() => ({ openedSettings })),
        openedEditModal: false,
        setOpenedEditModal: (openedEditModal: boolean) => set(() => ({ openedEditModal })),
        moveHistory: [],
        setMoveHistory: (moveHistory: MoveWithComment[]) => set(() => ({ moveHistory })),
        evaluations: {},
        addEvaluation: (halfMoveNumber: number, evaluation: Evaluation) =>
            set((state) => ({ evaluations: { ...state.evaluations, [halfMoveNumber]: evaluation } })),
        setEvaluations: (evaluations: Record<number, Evaluation>) => set(() => ({ evaluations })),
        redoStack: [],
        setRedoStack: (redoStack: MoveWithComment[]) => set(() => ({ redoStack })),
        bestMoves: [],
        setBestMoves: (bestMoves: string[]) => set(() => ({ bestMoves })),
        addBestMove: (bestMove: string) =>
            set((state) => {
                if (state.bestMoves[state.bestMoves.length - 1] === bestMove) return state;
                return { bestMoves: [...state.bestMoves, bestMove] };
            }),
        stockfishEval: undefined,
        setStockfishEval: (
            stockfishEval:
                | { positionEvaluation: string; depth: number; time: string; nps: string; isComplete: boolean }
                | undefined,
        ) => set(() => ({ stockfishEval })),
        bufferedMove: undefined,
        setBufferedMove: (bufferedMove: Move | undefined) => set(() => ({ bufferedMove })),
    }),
);

interface MistakesStorage {
    mistakeAttempts: OtAttempt2[];
    setMistakeAttempts: (mistakeAttempts: OtAttempt2[]) => void;
    currentIndex: number;
    setCurrentIndex: (currentIndex: number) => void;
}

// persistent storage
export const useMistakesStorage = create<MistakesStorage>()(
    devtools(
        persist(
            (set) => ({
                mistakeAttempts: [],
                setMistakeAttempts: (mistakeAttempts: OtAttempt2[]) => set(() => ({ mistakeAttempts })),
                currentIndex: 0,
                setCurrentIndex: (currentIndex: number) => set(() => ({ currentIndex })),
            }),
            {
                name: "mistakes-storage",
            },
        ),
    ),
);

interface ExclusionStorage {
    excludedMoves: string[];
    setExcludedMoves: (moves: string[]) => void;
}

export const useExclusionsStorage = create<ExclusionStorage>()((set) => ({
    excludedMoves: [],
    setExcludedMoves: (excludedMoves: string[]) => set(() => ({ excludedMoves })),
}));

export interface ExplorerStorage {
    includeTrainingModeGames: boolean;
    setIncludeTrainingModeGames: (includeTrainingModeGames: boolean) => void;
    fromDate?: string | null;
    setFromDate: (fromDate: string | null) => void;
    toDate?: string | null;
    setToDate: (toDate: string | null) => void;
    fromRating: number;
    toRating: number;
    setFromRating: (fromRating: number) => void;
    setToRating: (toRating: number) => void;
}

export const useExplorerStorage = create<ExplorerStorage>()(
    devtools(
        (set) => ({
            includeTrainingModeGames: false,
            setIncludeTrainingModeGames: (includeTrainingModeGames: boolean) =>
                set(() => ({ includeTrainingModeGames })),
            fromDate: undefined,
            setFromDate: (fromDate: string | null) => set(() => ({ fromDate })),
            toDate: undefined,
            setToDate: (toDate: string | null) => set(() => ({ toDate })),
            fromRating: 0,
            toRating: 3000,
            setFromRating: (fromRating: number) => set(() => ({ fromRating })),
            setToRating: (toRating: number) => set(() => ({ toRating })),
        }),
        {
            name: "explorer-storage",
        },
    ),
);

interface PersistedState {
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;
    selectedPositionID?: number;
    setSelectedPositionID: (selectedPosition: number | undefined) => void;
}

export const usePersistedStorage = create<PersistedState>()(
    devtools(
        persist(
            (set) => ({
                rowsPerPage: 5,
                setRowsPerPage: (rowsPerPage: number) => set(() => ({ rowsPerPage })),
                selectedPositionID: -1,
                setSelectedPositionID: (selectedPositionID: number | undefined) => set(() => ({ selectedPositionID })),
            }),
            {
                name: "PersistedStorage",
                version: 2,
            },
        ),
    ),
);

type SettingsStorage = Settings & { setSettings: (settings: Settings) => void };

export const useSettingsStorage = create<SettingsStorage>()(
    devtools(
        persist(
            (set) => ({
                timeControls: ["blitz", "rapid"],
                showEval: true,
                startFromInitial: false,
                rejectionThreshold: 0,
                preferDubious: false,
                setSettings: (settings: Settings) => set(() => settings),
                startRating: 1100,
                gameDuration: "long",
                allowMultipleLevels: false,
                isRated: true,
                engineSource: "lichess",
                lichessPlayer: undefined,
                lichessPlayerSettings: {
                    sinceYear: "2010",
                    sinceMonth: "January",
                    untilYear: "2025",
                    untilMonth: "December",
                },
                boardColor: "brown",
                whiteRepo: undefined,
                blackRepo: undefined,
                mistakeMode: "all",
                showCapturedPieces: true,
                useLocalStockfish: false,
                stockfishDepth2: "Auto",
                displayStockfishInfo: false,
            }),
            {
                name: "settings-storage", // name of the item in the storage (must be unique)
            },
        ),
    ),
);

//
export interface LoggedOutStorage {
    whiteRating?: number;
    blackRating?: number;
    blackAttempts: Attempt[];
    whiteAttempts: Attempt[];
    setLoggedOutData: (data: LoggedOutStorage) => void;
}

export const useLoggedOutStorage = create<LoggedOutStorage>()(
    devtools(
        persist(
            (set) => ({
                whiteRating: undefined,
                blackRating: undefined,
                whiteAttempts: [],
                blackAttempts: [],
                setLoggedOutData: (data: LoggedOutStorage) => set(() => data),
            }),
            {
                name: "logged-out-storage",
            },
        ),
    ),
);

// [
//     {
//         pgn: "1. e3 {-} e6 {0.1} 2. d4 {-} Nf6 {0.0} 3. Qd2 {-0.26}",
//         evaluation: 1.1,
//         type: "brilliant",
//     },
//     {
//         pgn: "1. e3 {-} e6 {0.1} 2. d4 {-} Nf6 {0.0} 3. Qd2 {-0.26}",
//         evaluation: -0.3,
//         type: "okay",
//     },
//     {
//         pgn: "1. e4 {-} d5 {0.5} 2. Nf3 {-} dxe4 {-0.7} 3. Qe2 {-5.19}",
//         evaluation: -5.2,
//         type: "bad",
//     },
//     {
//         pgn: "1. e4 {-} d5 {0.5} 2. Nf3 {-} dxe4 {-0.7} 3. Qe2 {-5.19}",
//         evaluation: -5.2,
//         type: "bad",
//     },
// ];
